import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import useMainNavigationStore from "../stores/useMainNavigationStore";

/**
 * Manages dropdown state using the central MainNavigation store.
 * Only one dropdown can be open at a time across all navigation components.
 */
type UseMainNavigationDropdownManagerReturn = {
  isOpen: boolean;
  dropdownId: string;
  openDropdown: () => void;
  closeDropdown: () => void;
  toggleDropdown: () => void;
};

const useMainNavigationDropdownManager = (id: string): UseMainNavigationDropdownManagerReturn => {
  const dropdownId = `dropdown-${id}`;
  const { setActiveDropdownMenu, closeDropdownMenu, isOpen } = useMainNavigationStore(
    useShallow((state) => ({
      setActiveDropdownMenu: state.setActiveDropdownMenu,
      closeDropdownMenu: state.closeDropdownMenu,
      isOpen: state.activeDropdownGroupId === dropdownId,
    }))
  );

  const handleOpenDropdown = useCallback(() => {
    setActiveDropdownMenu(dropdownId);
  }, [setActiveDropdownMenu, dropdownId]);

  const handleCloseDropdown = useCallback(() => {
    closeDropdownMenu(dropdownId);
  }, [closeDropdownMenu, dropdownId]);

  const handleToggleDropdown = useCallback(() => {
    if (isOpen) {
      handleCloseDropdown();
    } else {
      handleOpenDropdown();
    }
  }, [isOpen, handleOpenDropdown, handleCloseDropdown]);

  // Handle Escape key and outside clicks for keyboard accessibility
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseDropdown();
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      // Don't close if clicking the trigger (allows toggle behavior)
      if (target.closest(`[data-dropdown-id="${dropdownId}"]`)) {
        return;
      }

      // Don't close if clicking inside the menu
      if (target.closest('[role="menu"]')) {
        return;
      }

      // Close on any other outside click
      handleCloseDropdown();
    };

    // Use capture phase to ensure we catch events before React Aria
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    document.addEventListener("mousedown", handleOutsideClick, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("mousedown", handleOutsideClick, { capture: true });
    };
  }, [isOpen, handleCloseDropdown, dropdownId]);

  return {
    isOpen,
    dropdownId,
    openDropdown: handleOpenDropdown,
    closeDropdown: handleCloseDropdown,
    toggleDropdown: handleToggleDropdown,
  };
};

export default useMainNavigationDropdownManager;
